import { useRef, type ReactNode } from 'react';

import { useFlies } from '@/hooks/useFlies';

import styles from './ExpiredFlies.module.css';

const COUNT = 3;

/** One fly, 8 by 8 px: two translucent wings that flutter, a small body and a head, the head up. */
function Fly({ index, setRef }: { readonly index: number; readonly setRef: (element: HTMLSpanElement | null) => void }): React.JSX.Element {
  return (
    <span ref={setRef} className={styles.fly} aria-hidden="true" data-testid="fly" data-fly={index}>
      <svg viewBox="0 0 8 8" width="8" height="8" focusable="false">
        <g transform="rotate(-25 3.2 4)">
          <ellipse className={styles.wing} cx="1.6" cy="4" rx="3.4" ry="2" />
        </g>
        <g transform="rotate(25 4.8 4)">
          <ellipse className={styles.wing} cx="6.4" cy="4" rx="3.4" ry="2" />
        </g>
        <ellipse className={styles.body} cx="4" cy="5" rx="2.2" ry="3" />
        <circle className={styles.body} cx="4" cy="1.8" r="1.8" />
      </svg>
    </span>
  );
}

/**
 * The title of a bridge that has expired, with three tiny flies about it. Only for decoration: they are hidden from
 * screen readers, take no clicks, and fly in `hooks/useFlies.ts` (still, under reduced motion).
 */
export function ExpiredTitle({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const host = useRef<HTMLSpanElement>(null);
  const flies = useRef<(HTMLElement | null)[]>([]);
  useFlies(host, flies, COUNT);
  return (
    <span ref={host} className={styles.host}>
      {children}
      {Array.from({ length: COUNT }, (_, index) => (
        <Fly
          key={index}
          index={index}
          setRef={(element) => {
            flies.current[index] = element;
          }}
        />
      ))}
    </span>
  );
}
